import React, { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Plug, Brain, Sparkles, Code, Globe, Megaphone, GitBranch,
  Server, Check, X, Loader2, Eye, EyeOff, TestTube, Unplug,
  ChevronRight, Shield, AlertCircle, ExternalLink,
} from "lucide-react";

const ICON_MAP: Record<string, React.FC<any>> = {
  brain: Brain,
  sparkles: Sparkles,
  code: Code,
  globe: Globe,
  megaphone: Megaphone,
  "git-branch": GitBranch,
  server: Server,
};

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  connected: { bg: "bg-emerald-500/10", text: "text-emerald-500", label: "Conectado" },
  partial: { bg: "bg-amber-500/10", text: "text-amber-500", label: "Parcial" },
  disconnected: { bg: "bg-muted", text: "text-muted-foreground", label: "Desconectado" },
};

export function Integrations() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
          <Plug className="w-6 h-6 text-primary" />
          Integrações
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Configure as integrações da sua organização com provedores de IA e serviços externos
        </p>
      </div>

      <DefaultProviderSelector />

      {selectedId ? (
        <IntegrationDetail
          integrationId={selectedId}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <IntegrationsList onSelect={setSelectedId} />
      )}
    </div>
  );
}

function DefaultProviderSelector() {
  const { data } = trpc.integrations.getDefaultProvider.useQuery();
  const utils = trpc.useUtils();
  const setProvider = trpc.integrations.setDefaultProvider.useMutation({
    onSuccess: () => utils.integrations.getDefaultProvider.invalidate(),
  });

  const providers = [
    { id: "manus", label: "Manus AI", icon: Brain },
    { id: "anthropic", label: "Claude", icon: Sparkles },
    { id: "openai", label: "OpenAI / Codex", icon: Code },
    { id: "google_ai", label: "Gemini", icon: Globe },
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-foreground">Provedor Padrão de IA</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {providers.map((p) => (
          <button
            key={p.id}
            onClick={() => setProvider.mutate({ provider: p.id })}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
              data?.provider === p.id
                ? "bg-primary/10 text-primary border-primary/30"
                : "bg-secondary/50 text-muted-foreground border-border hover:text-foreground hover:border-primary/20"
            }`}
          >
            <p.icon className="w-4 h-4" />
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function IntegrationsList({ onSelect }: { onSelect: (id: string) => void }) {
  const { data: integrations, isLoading } = trpc.integrations.list.useQuery();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="h-10 w-10 bg-muted rounded-xl mb-3" />
            <div className="h-5 bg-muted rounded w-2/3 mb-2" />
            <div className="h-3 bg-muted rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  const categories = [
    { id: "ai_provider", label: "Provedores de IA" },
    { id: "analytics", label: "Analytics e Ads" },
    { id: "automation", label: "Automação e DevOps" },
  ];

  return (
    <div className="space-y-6">
      {categories.map((cat) => {
        const items = integrations?.filter((i: any) => i.category === cat.id) || [];
        if (items.length === 0) return null;
        return (
          <div key={cat.id}>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              {cat.label}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((integration: any) => {
                const Icon = ICON_MAP[integration.icon] || Plug;
                const status = STATUS_STYLES[integration.status] || STATUS_STYLES.disconnected;
                return (
                  <button
                    key={integration.id}
                    onClick={() => onSelect(integration.id)}
                    className="bg-card border border-border rounded-xl p-5 text-left hover:border-primary/30 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </div>
                    <h3 className="font-semibold text-foreground mb-1 flex items-center gap-1">
                      {integration.name}
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {integration.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IntegrationDetail({ integrationId, onBack }: { integrationId: string; onBack: () => void }) {
  const { data: integration, isLoading } = trpc.integrations.get.useQuery({ integrationId });
  const utils = trpc.useUtils();
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const saveMutation = trpc.integrations.save.useMutation({
    onSuccess: () => {
      utils.integrations.get.invalidate({ integrationId });
      utils.integrations.list.invalidate();
      setCredentials({});
    },
  });

  const testMutation = trpc.integrations.test.useMutation({
    onSuccess: (result) => setTestResult(result),
  });

  const disconnectMutation = trpc.integrations.disconnect.useMutation({
    onSuccess: () => {
      utils.integrations.get.invalidate({ integrationId });
      utils.integrations.list.invalidate();
    },
  });

  if (isLoading || !integration) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const Icon = ICON_MAP[integration.icon] || Plug;
  const allConfigured = integration.keys.every((k: any) => k.configured);

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
      >
        &larr; Voltar para integrações
      </button>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{integration.name}</h2>
              <p className="text-sm text-muted-foreground">{integration.description}</p>
            </div>
          </div>
          <a
            href={integration.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            Docs <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Credential fields */}
        <div className="space-y-4">
          {integration.keys.map((key: any) => (
            <div key={key.key}>
              <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
                {key.label}
                {key.configured && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500">
                    Configurado
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  type={key.secret && !showValues[key.key] ? "password" : "text"}
                  placeholder={key.configured ? key.maskedValue || "****" : key.placeholder}
                  value={credentials[key.key] || ""}
                  onChange={(e) =>
                    setCredentials((prev) => ({ ...prev, [key.key]: e.target.value }))
                  }
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 pr-10"
                />
                {key.secret && (
                  <button
                    onClick={() =>
                      setShowValues((prev) => ({ ...prev, [key.key]: !prev[key.key] }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showValues[key.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Test result */}
        {testResult && (
          <div
            className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
              testResult.success
                ? "bg-emerald-500/10 text-emerald-500"
                : "bg-red-500/10 text-red-500"
            }`}
          >
            {testResult.success ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {testResult.message}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-border">
          <button
            onClick={() => saveMutation.mutate({ integrationId, credentials })}
            disabled={saveMutation.isPending || Object.values(credentials).every((v) => !v)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Salvar Credenciais
          </button>

          {allConfigured && (
            <>
              <button
                onClick={() => {
                  setTestResult(null);
                  testMutation.mutate({ integrationId });
                }}
                disabled={testMutation.isPending}
                className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 transition-colors disabled:opacity-50"
              >
                {testMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4" />
                )}
                Testar Conexão
              </button>

              <button
                onClick={() => {
                  if (confirm("Desconectar esta integração? As credenciais serão desativadas.")) {
                    disconnectMutation.mutate({ integrationId });
                  }
                }}
                disabled={disconnectMutation.isPending}
                className="flex items-center gap-2 px-4 py-2.5 text-red-500 hover:bg-red-500/10 rounded-lg text-sm font-medium transition-colors ml-auto"
              >
                <Unplug className="w-4 h-4" />
                Desconectar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
